import type { MouseEvent } from "react";
import { ChevronRight, CircuitBoard, Cpu, Link2 } from "lucide-react";
import { useCards } from "@/features/cards/hooks";
import type { CardSummary } from "@/features/cards/hooks";
import type { SensorSummary } from "@/features/sensors/hooks";
import { Dot } from "@/components/ui/dot";
import { cn } from "@/lib/utils";
import type { Selection, SensorsByCardId } from "@/types/device-manager.types";
import type { DeviceSummary } from "@/features/devices/hooks";
import { SENSOR_STATUS_TONE } from "./sensor-display";
import { devicePresence, PRESENCE_TONE } from "./device-presence";

export interface TreeCtx {
  query: string;
  sel: Selection;
  expanded: Set<string>;
  sensorsByCardId: SensorsByCardId;
  onToggle: (id: string) => void;
  onSelect: (s: Selection) => void;
}

const norm = (q: string) => q.trim().toLowerCase();
const hit = (q: string, ...vals: (string | null | undefined)[]) =>
  !q || vals.some((v) => (v ?? "").toLowerCase().includes(q));

function Twist({ open, onClick }: { open: boolean; onClick: (e: MouseEvent) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? "Recolher" : "Expandir"}
      className="rounded p-0.5 text-text-tertiary hover:text-text-primary"
    >
      <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} />
    </button>
  );
}

const ROW =
  "flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left hover:bg-bg-muted-rd";
const ACTIVE = "bg-bg-muted-rd ring-1 ring-border-default";

function SensorLeaf({
  clpId,
  cardId,
  sensor,
  ctx,
}: {
  clpId: string;
  cardId: string;
  sensor: SensorSummary;
  ctx: TreeCtx;
}) {
  const active = ctx.sel?.type === "sensor" && ctx.sel.sensorId === sensor.id;
  return (
    <button
      type="button"
      onClick={() => ctx.onSelect({ type: "sensor", clpId, cardId, sensorId: sensor.id })}
      className={cn(ROW, "pl-7", active && ACTIVE)}
    >
      <Dot tone={SENSOR_STATUS_TONE[sensor.status]} halo={false} size={7} />
      <span className="truncate font-mono text-[12px] text-text-primary">{sensor.code}</span>
      {sensor.position != null && (
        <span className="font-mono text-[11px] text-text-tertiary">pos {sensor.position}</span>
      )}
      {sensor.activeLink && <Link2 className="h-3 w-3 text-brand-500" />}
    </button>
  );
}

function CardBranch({ clpId, card, ctx }: { clpId: string; card: CardSummary; ctx: TreeCtx }) {
  const q = norm(ctx.query);
  const sensors = ctx.sensorsByCardId.get(card.id) ?? [];
  const cardMatch = hit(q, card.code, card.name);
  const matchingSensors = sensors.filter((s) => hit(q, s.code, s.name));
  if (q && !cardMatch && matchingSensors.length === 0) return null;

  const open = ctx.expanded.has(card.id) || Boolean(q);
  const active = ctx.sel?.type === "card" && ctx.sel.cardId === card.id;
  const shown = q && !cardMatch ? matchingSensors : sensors;

  return (
    <div>
      <div className={cn(ROW, "pl-4", active && ACTIVE)}>
        <Twist
          open={open}
          onClick={(e) => {
            e.stopPropagation();
            ctx.onToggle(card.id);
          }}
        />
        <button
          type="button"
          onClick={() => ctx.onSelect({ type: "card", clpId, cardId: card.id })}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <CircuitBoard className="h-3.5 w-3.5 text-text-tertiary" />
          <span className="truncate font-mono text-[12px] text-text-primary">{card.code}</span>
          <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
            {card.type}
          </span>
          <span className="text-[11px] text-text-tertiary">{sensors.length}</span>
        </button>
      </div>
      {open &&
        shown.map((s) => (
          <SensorLeaf key={s.id} clpId={clpId} cardId={card.id} sensor={s} ctx={ctx} />
        ))}
    </div>
  );
}

export function DeviceBranch({
  device,
  sensorCount,
  ctx,
}: {
  device: DeviceSummary;
  sensorCount: number;
  ctx: TreeCtx;
}) {
  const q = norm(ctx.query);
  const hasQuery = Boolean(q);
  const open = ctx.expanded.has(device.id) || hasQuery;
  const cards = useCards(device.id, { enabled: open }).data ?? [];
  const deviceMatch = hit(q, device.code, device.name);

  const visibleCards =
    hasQuery && !deviceMatch
      ? cards.filter((c) => {
          const sensors = ctx.sensorsByCardId.get(c.id) ?? [];
          return hit(q, c.code, c.name) || sensors.some((s) => hit(q, s.code, s.name));
        })
      : cards;

  if (hasQuery && !deviceMatch && visibleCards.length === 0) return null;

  const active = ctx.sel?.type === "clp" && ctx.sel.clpId === device.id;
  return (
    <div>
      <div className={cn(ROW, active && ACTIVE)}>
        <Twist
          open={open}
          onClick={(e) => {
            e.stopPropagation();
            ctx.onToggle(device.id);
          }}
        />
        <button
          type="button"
          onClick={() => ctx.onSelect({ type: "clp", clpId: device.id })}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <Cpu className="h-4 w-4 text-text-tertiary" />
          <Dot tone={PRESENCE_TONE[devicePresence(device.lastSeenAt)]} halo={false} size={6} />
          <span className="truncate font-mono text-[13px] text-text-primary">{device.code}</span>
          <span className="text-[11px] text-text-tertiary">
            {device.cardCount ?? cards.length} cart · {sensorCount} sens
          </span>
        </button>
      </div>
      {open && (
        <div>
          {visibleCards.map((c) => (
            <CardBranch key={c.id} clpId={device.id} card={c} ctx={ctx} />
          ))}
        </div>
      )}
    </div>
  );
}
